/**
 * Parse a `metadata` AWSJSON field into a plain object.
 *
 * The GraphQL `metadata` field is `AWSJSON`, which reaches the client either as a
 * JSON string or an already-parsed object depending on the resolver — handle both.
 * Anything missing, malformed, or not an object yields `undefined`. Never spread a
 * raw `metadata` value directly — if it's still a string, `{ ...metadata }` silently
 * produces numeric-index keys per character instead of the intended fields.
 */
export const parseMetadata = (metadata: unknown): Record<string, unknown> | undefined => {
    if (!metadata) return undefined;

    let parsed: unknown = metadata;
    if (typeof metadata === 'string') {
        try {
            parsed = JSON.parse(metadata);
        } catch {
            return undefined;
        }
    }

    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined;
};

/**
 * Read a string field from a subproject's `metadaten` JSON.
 *
 * The approval flow writes `{ serviceRequestId, sourceSystem }` into
 * `subprojektMetadata` (see ucpw-services create-project / create-subproject).
 */
const readMetadataString = (metadata: unknown, field: string): string | undefined => {
    const value = parseMetadata(metadata)?.[field];
    return typeof value === 'string' ? value : undefined;
};

/**
 * The integration channel a subproject originated from ('gothaer' | 'pxconnect').
 * Gates whether the PX Connect Fragenkatalog is offered; missing → undefined (hide).
 */
export const sourceSystemFromMetadata = (metadata: unknown): string | undefined =>
    readMetadataString(metadata, 'sourceSystem');

/** True when the subproject originated from a PX Connect order. */
export const isPxConnectSource = (metadata: unknown): boolean =>
    sourceSystemFromMetadata(metadata) === 'pxconnect';

/**
 * Versicherung des Auftrags, von der Annahme in die Subprojekt-Metadaten geschrieben
 * (UCPWO-398, ucpw-services create-project / create-subproject). Projekte von vor dieser
 * Änderung tragen sie nicht → undefined.
 */
export const insurerFromMetadata = (
    metadata: unknown
): { number: string | null; name: string | null } | undefined => {
    const insurer = parseMetadata(metadata)?.insurer;
    if (!insurer || typeof insurer !== 'object') return undefined;
    const { number, name } = insurer as Record<string, unknown>;
    const asText = (value: unknown) => (typeof value === 'string' && value ? value : null);
    if (!asText(number) && !asText(name)) return undefined;
    return { number: asText(number), name: asText(name) };
};

/**
 * Verbindung, von der SVC den Auftrag geholt hat (UCPWO-398). Nur die drei bekannten
 * Arten; alles andere und fehlende Angaben → undefined, nie eine Ableitung aus der Stage.
 */
export const connectionFromMetadata = (
    metadata: unknown
): { kind: 'prod' | 'test' | 'emulate'; host: string } | undefined => {
    const connection = parseMetadata(metadata)?.connection;
    if (!connection || typeof connection !== 'object') return undefined;
    const { kind, host } = connection as Record<string, unknown>;
    if (typeof host !== 'string' || !host) return undefined;
    if (kind !== 'prod' && kind !== 'test' && kind !== 'emulate') return undefined;
    return { kind, host };
};

/**
 * Ob der Projekttyp dieses Subprojekts nur geraten wurde (UCPWO-410). Kam der Auftrag mit
 * leerem oder unbekanntem `orderType`, legt die Annahme ihn als Leckortung an und schreibt
 * `auftragstyp: { roh, rueckfall: true }` in die Subprojekt-Metadaten. Projekte von vor
 * dieser Änderung tragen den Vermerk nicht → undefined.
 */
export const auftragstypFromMetadata = (
    metadata: unknown
): { roh: string | null; rueckfall: boolean } | undefined => {
    const auftragstyp = parseMetadata(metadata)?.auftragstyp;
    if (!auftragstyp || typeof auftragstyp !== 'object') return undefined;
    const { roh, rueckfall } = auftragstyp as Record<string, unknown>;
    if (typeof rueckfall !== 'boolean') return undefined;
    return { roh: typeof roh === 'string' && roh ? roh : null, rueckfall };
};

/**
 * The originating `serviceRequestId`. Keys the PX Connect Fragenkatalog query
 * (`serviceRequestQuestionCatalog`); missing → undefined (no catalog available).
 */
export const serviceRequestIdFromMetadata = (metadata: unknown): string | undefined =>
    readMetadataString(metadata, 'serviceRequestId');

/**
 * Höchstlänge des Anzeigewerts (UCPWO-418): eine Kennung wie „02“, kein Freitext. Dieselbe
 * Grenze prüft die API (`ORDER_NUMBER_DISPLAY_MAX_LENGTH` in api/code/api/src/order-number.ts).
 */
export const ORDER_NUMBER_DISPLAY_MAX_LENGTH = 20;

/**
 * Auftrags-ID eines Subprojekts (UCPWO-418). Die Annahme schreibt die ServicePool-Auftrags-ID
 * (`vorsatz.absender.absOrdNrDLP`) als `orderNumber` in die Subprojekt-Metadaten (UCPWO-412);
 * eine Kopie übernimmt sie von ihrer Vorlage. Über sie gehen alle Antworten an den Auftrag —
 * sie ändert sich nie.
 *
 * `label` ist der Anzeigewert `orderNumberDisplay`, den Pöppinghaus z. B. auf die „02“ aus dem
 * Auftrags-PDF umbenennen kann, sonst die ID selbst — für Vergleiche. Angezeigt wird
 * `shortLabel`: nach der Höchstlänge abgeschnitten, damit ein Wert von vor der Grenze keine
 * Tabelle und keinen Dialog sprengt. Von Hand angelegte und alte Subprojekte tragen keine ID →
 * undefined.
 */
export const orderNumberFromMetadata = (
    metadata: unknown
): { orderNumber: string; label: string; shortLabel: string } | undefined => {
    const orderNumber = readMetadataString(metadata, 'orderNumber')?.trim();
    if (!orderNumber) return undefined;
    const display = readMetadataString(metadata, 'orderNumberDisplay')?.trim();
    const label = display || orderNumber;
    const shortLabel =
        label.length > ORDER_NUMBER_DISPLAY_MAX_LENGTH
            ? `${label.slice(0, ORDER_NUMBER_DISPLAY_MAX_LENGTH)}…`
            : label;
    return { orderNumber, label, shortLabel };
};

/**
 * Ob zwei Anzeigewerte als gleich gelten (UCPWO-418): Leerzeichen am Rand und Groß-/
 * Kleinschreibung zählen nicht. Dieselbe Regel prüft die API beim Speichern.
 */
export const isSameOrderLabel = (a: string, b: string): boolean =>
    a.trim().toLocaleLowerCase('de-DE') === b.trim().toLocaleLowerCase('de-DE');

/**
 * Pick the integration metadata that stands for a whole project.
 *
 * `sourceSystem`/`serviceRequestId` are only ever written per subproject
 * (`subprojektMetadata`, see ucpw-services create-project / create-subproject) — a Projekt
 * itself never carries them. Views without an active subproject (Projektüberblick) therefore
 * take the first subproject that actually names one: subproject order is not guaranteed and
 * manually created subprojects have no such metadata at all.
 */
export const sourceMetadataFromSubprojects = (
    subprojects: ReadonlyArray<{ metadata?: unknown } | null | undefined> | null | undefined
): unknown =>
    subprojects?.find(
        (subproject) =>
            sourceSystemFromMetadata(subproject?.metadata) ||
            serviceRequestIdFromMetadata(subproject?.metadata)
    )?.metadata;

/**
 * True when ANY subproject of the project originated from PX Connect.
 *
 * The project-level question, as opposed to `isPxConnectSource`, which answers it for a
 * single subproject. Both used to be called "is PX Connect" at the call site, and the two
 * answers differ exactly when a subproject was created by hand: the project still belongs
 * to a PX Connect order, that one subproject does not (PXC-B-04).
 *
 * Use this for things that belong to the whole project — the origin badge, the appointment
 * button. Anything that needs to know WHICH order (the Fragenkatalog is keyed by
 * `serviceRequestId`) must stay on the active subproject: a project can carry subprojects
 * from two different orders, and picking "the first one found" would show a foreign
 * catalog without saying so.
 */
export const isPxConnectProject = (
    subprojects: ReadonlyArray<{ metadata?: unknown } | null | undefined> | null | undefined
): boolean => isPxConnectSource(sourceMetadataFromSubprojects(subprojects));
